import { faEllipsis } from "@fortawesome/free-solid-svg-icons/faEllipsis";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons/faPenToSquare";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons/faTrashCan";
import { TFunction } from "i18next";
import styled from "styled-components";
import type { ColumnType, MenuProps } from "../../../utils/local-antd";

import { AtiraDropdown } from "../../../components/AtiraDropdown";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { TextWithTruncated } from "../../../components/TextWithTruncated";
import { User } from "../../../model/user/User";
import { Spacing } from "../../../theme/Spacing";
import { standardDate } from "../../../utils/Date";
import { BaseModuleInstance } from "../../../model/module/types/module-instance/BaseModuleInstance";
import { ModuleDefinition } from "../../../model/module/types/module-definition/ModuleDefinition";
import { AssigneeBanner } from "../../../components/AssigneeBanner";
import { faUser } from "@fortawesome/free-solid-svg-icons/faUser";
import { TableActionableCellEmail } from "../../../components/table/TableActionableCellEmail";
import isString from "lodash/isString";
import { Regexes } from "../../../utils/String";
import { TableActionableCellPhone } from "../../../components/table/TableActionableCellPhone";
import { ModuleDefinitionKind } from "../../../model/module/types/module-definition/ModuleDefinitionKind";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import isEmpty from "lodash/isEmpty";
import { faLink } from "@fortawesome/free-solid-svg-icons/faLink";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { ModuleDefinitionPermissions } from "../../../model/module/types/module-definition/ModuleDefinitionPermissions";
import { faPaperclip } from "@fortawesome/free-solid-svg-icons/faPaperclip";
import { isDateModuleInput } from "../module-definition/utils/moduleUtils";
import { TableActionableCellPreview } from "../../../components/table/TableActionableCellPreview";
import { getEntryLabel } from "../../../utils/entryUtil";
import isNumber from "lodash/isNumber";
import isNil from "lodash/isNil";
import uniqBy from "lodash/uniqBy";

const ActionContainer = styled(Flex)`
  align-items: center;
  gap: ${Spacing.s};

  svg {
    width: 1rem;
  }
`;

type ModuleInstanceATVoidFunction = ATVoidFunction<BaseModuleInstance>;
type FieldFunctions = {
  deleteInstance: ModuleInstanceATVoidFunction;
  updateInstance: ModuleInstanceATVoidFunction;
  previewInstance: ModuleInstanceATVoidFunction;
  previewEntry: ModuleInstanceATVoidFunction;
  attachToEntry: ModuleInstanceATVoidFunction;
  viewAttachedEntries: ModuleInstanceATVoidFunction;
  t: TFunction;
};

// Define a type that includes all keys of ModuleInstance plus the 'actions' key
type InstanceKeys =
  | keyof BaseModuleInstance
  | "actions"
  | "ids"
  | "createdBy"
  | "instance-name";

const TableColumnKeys: InstanceKeys[] = [
  "actions",
  "ids",
  "createdBy",
  "instance-name",
  "instanceAttachments",
  "entryId",
  "createdAt",
];

const getFieldWidth = (field: string) => {
  switch (true) {
    case field === "actions" || field === "ids" || field === "createdBy":
      return 100;
    case field === "instance-name":
      return 150;
    case field === "instanceAttachments":
      return 300;
    default:
      return 250;
  }
};

const getRenderField = (
  instance: BaseModuleInstance,
  moduleDefinition: ModuleDefinition | null,
  key: string,
  user?: User,
  asText = false,
) => {
  const value = instance.fields?.[key];

  if (isDateModuleInput(moduleDefinition!, key)) {
    const dateTimezone = moduleDefinition?.inputs?.find(
      (i) => i.name === key,
    )?.dateInputConfig?.timezone;

    if (asText) {
      return standardDate(value as Date, user, true, false, dateTimezone);
    }

    return (
      <TextWithTruncated length={30}>
        {/* @ts-ignore */}
        {standardDate(value as Date, user, true, false, dateTimezone)}
      </TextWithTruncated>
    );
  }

  if (isString(value) && Regexes.EMAIL_REGEX.test(value)) {
    if (asText) {
      return value;
    }

    return (
      <TableActionableCellEmail
        email={value}
        extra={{ kind: instance.entry?.kind }}
      />
    );
  }

  if (isString(value) && Regexes.PHONE_REGEX.test(value)) {
    if (asText) {
      return value;
    }

    return (
      <TableActionableCellPhone
        phone={value}
        extra={{ kind: instance.entry?.kind }}
      />
    );
  }

  if (asText) {
    return isDateModuleInput(moduleDefinition!, key)
      ? standardDate<string>(value as Date, user, true)
      : value || "";
  }

  return (
    <TextWithTruncated length={30}>
      {/* @ts-ignore */}
      {isDateModuleInput(moduleDefinition!, key)
        ? standardDate(
            value as Date,
            user,
            true,
            false,
            moduleDefinition?.inputs?.find((i) => i.name === key)
              ?.dateInputConfig?.timezone,
          )
        : value || ""}
    </TextWithTruncated>
  );
};

const getFieldInfo = (
  column: InstanceKeys,
  functions: FieldFunctions,
  user?: User,
  members?: User[],
  isFixedModule?: boolean,
  modulePermissions?: ModuleDefinitionPermissions["permissions"],
) => {
  const {
    t,
    deleteInstance,
    updateInstance,
    previewInstance,
    previewEntry,
    attachToEntry,
    viewAttachedEntries,
  } = functions;

  switch (column) {
    case "actions":
      return {
        title: t("common.actions"),
        key: "actions",
        render: (instance: BaseModuleInstance) => {
          const items: MenuProps["items"] = [
            {
              key: "g1",
              type: "group",
              children: [
                {
                  label: (
                    <ActionContainer>
                      <AtiraIcon icon={faEye} color="main" />
                      <Text color="main">{t("common.preview")}</Text>
                    </ActionContainer>
                  ),
                  onClick: () => previewInstance(instance),
                },
                {
                  label: (
                    <ActionContainer>
                      <AtiraIcon icon={faPenToSquare} color="main" />
                      <Text color="main">{t("common.edit")}</Text>
                    </ActionContainer>
                  ),
                  onClick: () => updateInstance(instance),
                  disabled: modulePermissions?.UPDATE === false,
                },
                {
                  label: (
                    <ActionContainer>
                      <AtiraIcon icon={faUser} color="main" />
                      <Text color="main">
                        {t("modules.instance.actions.viewEntry")}
                      </Text>
                    </ActionContainer>
                  ),
                  onClick: () => previewEntry(instance),
                  disabled: isFixedModule === false || isEmpty(instance?.entry),
                },
                {
                  label: (
                    <ActionContainer>
                      <AtiraIcon icon={faPaperclip} color="main" />
                      <Text color="main">
                        {t("modules.instance.actions.viewAttachments")}
                      </Text>
                    </ActionContainer>
                  ),
                  onClick: () => viewAttachedEntries(instance),
                  disabled:
                    !instance?.instanceAttachments?.length || !isFixedModule,
                },
                {
                  label: (
                    <ActionContainer>
                      <AtiraIcon icon={faLink} color="main" />
                      <Text color="main">
                        {t("modules.instance.actions.attach")}
                      </Text>
                    </ActionContainer>
                  ),
                  onClick: () => attachToEntry(instance),
                  disabled:
                    modulePermissions?.UPDATE === false || !isFixedModule,
                },
                {
                  label: (
                    <ActionContainer>
                      <AtiraIcon icon={faTrashCan} color="main" />
                      <Text color="main">{t("common.delete")}</Text>
                    </ActionContainer>
                  ),
                  onClick: () => deleteInstance(instance),
                  disabled: modulePermissions?.DELETE === false,
                },
              ]
                .filter((child) => child?.disabled !== true)
                .map((child, index) => ({
                  ...child,
                  key: index,
                  style: { padding: 4 },
                })),
            },
          ];

          return {
            children: (
              <Flex justifyContent="center">
                <AtiraDropdown items={items} placement="bottomLeft">
                  <Button
                    icon={faEllipsis}
                    backgroundColor="transparent"
                    margin="none"
                    color="black"
                  />
                </AtiraDropdown>
              </Flex>
            ),
          };
        },
        center: true,
        onCell: () => ({
          style: { minWidth: getFieldWidth(column) },
        }),
        data: { column, enabled: true },
        fixed: "left" as ColumnType["fixed"],
      };

    case "ids":
      return {
        title: t("modules.instance.ids.title"),
        key: "ids",
        render: (instance: BaseModuleInstance) => {
          return (
            <TextWithTruncated length={Lengths.NAME}>
              {isNumber(instance?.sequenceId)
                ? instance?.sequenceId.toString()
                : ""}
            </TextWithTruncated>
          );
        },
        sortable: true,
        fixed: "left" as ColumnType["fixed"],
        onCell: () => ({ style: { minWidth: getFieldWidth(column) } }),
      };

    case "instance-name":
      return {
        title: t("modules.instance.name.title"),
        key: "instance-name",
        render: (instance: BaseModuleInstance) => {
          return (
            <TextWithTruncated length={Lengths.NAME}>
              {instance?.name}
            </TextWithTruncated>
          );
        },
        sortable: true,
        reorder: true,
        onCell: () => ({
          style: { minWidth: getFieldWidth(column) },
        }),
      };

    case "createdBy":
      return {
        title: t("common.createdBy"),
        key: "createdBy",
        render: (instance: BaseModuleInstance) => {
          const assignees = uniqBy(
            members?.concat([user!]),
            (m) => m._id,
          ).filter((m) => m?._id === instance.userId);

          return (
            <AssigneeBanner
              hideSelf
              assignees={assignees}
              title={t("common.createdBy")}
            />
          );
        },
        onCell: () => ({
          style: { minWidth: getFieldWidth("createdBy") },
        }),
        fixed: "left" as ColumnType["fixed"],
      };

    case "entryId":
      return {
        title: t("common.entry"),
        key: "entryId",
        render: (moduleInstance: BaseModuleInstance) => {
          return moduleInstance?.entry ? (
            <TableActionableCellPreview
              text={getEntryLabel(moduleInstance.entry)}
              onPreview={() => previewEntry?.(moduleInstance)}
            />
          ) : null;
        },
        sortable: true,
        reorder: true,
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
        data: { column, enabled: true },
      };

    case "instanceAttachments":
      return {
        title: t("common.attachments"),
        key: "entries",
        render: (moduleInstance: BaseModuleInstance) => {
          const attachmentsNames = moduleInstance.instanceAttachments
            ?.map((e) => e?.name)
            .join(", ");

          return (
            <TableActionableCellPreview
              text={attachmentsNames}
              onPreview={() => viewAttachedEntries(moduleInstance)}
            />
          );
        },
        sortable: true,
        reorder: true,
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
        data: { column, enabled: true },
      };

    case "createdAt":
      return {
        title: t("common.createdAt"),
        key: "createdAt",
        render: ({ createdAt }: BaseModuleInstance) => (
          <Text>
            {createdAt
              ? standardDate<string>(new Date(createdAt), user, true)
              : null}
          </Text>
        ),
        sorter: (a: BaseModuleInstance, b: BaseModuleInstance) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateA - dateB;
        },
        sortable: true,
        reorder: true,
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
        data: { column, enabled: true },
      };

    default:
      return {
        title: t(`common.${column}`),
        key: column,
        render: (instance: BaseModuleInstance) => (
          <TextWithTruncated length={30}>
            {(instance[column] as string) || ""}
          </TextWithTruncated>
        ),
        sortable: true,
        reorder: true,
        data: { column, enabled: true },
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
      };
  }
};

export const createModuleInstancesColumns = (
  functions: FieldFunctions,
  user: User | undefined,
  moduleDefinition: ModuleDefinition | null,
  members?: User[],
  modulePermissions?: ModuleDefinitionPermissions["permissions"],
  extraKeys: string[] = [],
) => {
  const staticKeys = TableColumnKeys.filter((key) =>
    moduleDefinition?.kind === ModuleDefinitionKind.DYNAMIC
      ? !["instance-name", "instanceAttachments"].includes(key)
      : key !== "entryId",
  ) as InstanceKeys[];

  const moduleInputKeys =
    moduleDefinition?.inputs?.map((input) => input.name) || [];

  const getCurrentInputName = (fieldKey: string) => {
    return moduleDefinition?.inputs?.find((i) => i.name === fieldKey)?.label;
  };

  const isFixedModule = moduleDefinition?.kind === ModuleDefinitionKind.FIXED;

  const fieldColumns = moduleInputKeys.map((fieldKey) => ({
    title: getCurrentInputName(fieldKey) || functions.t("common.uncategorized"),
    key: `fields.${fieldKey}`,
    render: (instance: BaseModuleInstance) =>
      getRenderField(instance, moduleDefinition, fieldKey, user),
    sortable: true,
    reorder: true,
    onCell: () => ({
      style: { minWidth: getFieldWidth(fieldKey) },
    }),
    data: { column: fieldKey, enabled: true },
  }));

  const uncategorizedColumn = extraKeys.length
    ? [
        {
          title: functions.t("common.uncategorized"),
          key: "fields.__uncategorized__",
          render: (instance: BaseModuleInstance) => {
            const keysWithValue = extraKeys.filter((k) => {
              const value = instance.fields?.[k];
              return !isNil(value) && value !== "";
            });

            if (!keysWithValue.length) {
              return null;
            }

            return (
              <Flex>
                {keysWithValue.map((k, i, thisArray) => (
                  <Text key={k}>
                    {
                      getRenderField(
                        instance,
                        moduleDefinition,
                        k,
                        user,
                        true,
                      ) as string
                    }
                    {i < thisArray.length - 1 ? ", \u00A0" : null}
                  </Text>
                ))}
              </Flex>
            );
          },
          sortable: true,
          reorder: true,
          onCell: () => ({
            style: { minWidth: 300 },
          }),
          data: { column: "__uncategorized__", enabled: true },
        },
      ]
    : [];

  const staticColumns = staticKeys.map((key) =>
    getFieldInfo(
      key,
      functions,
      user,
      members,
      isFixedModule,
      modulePermissions,
    ),
  );

  return [...staticColumns, ...fieldColumns, ...uncategorizedColumn];
};
